import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BehaviorSubject, filter, tap } from "rxjs";
import { MessageService } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";

import { ProgressDialogComponent, ProgressDialogData } from "./progress-dialog/progress-dialog.component";
import { OrderSuccessDialogComponent } from "./order-success-dialog/order-success-dialog.component";

// Actions from other stores (for toast notifications)
import { QuoteActions } from "../../pages/quote/quote.actions";
import { QuoteCreateActions } from "../../pages/quote-create/quote/quote-create.actions";
// PreviewActions moved to QuoteActions
import { QuoteVersionActions } from "../../pages/quote/version/version.actions";
import { ArticlesActions, ProductCatalogActions } from "../../pages/quote/articles/articles.actions";
import { CustomerActions } from "../../pages/customer/customer.actions";
import { ContactActions } from "../../pages/contact/contact.actions";
import { UserActions } from "../../user/user.actions";
import { TransferQuotesActions } from "../../pages/admin/developer/transfer-quotes/transfer-quotes.actions";
import { WebhookActions } from "../../pages/admin/developer/webhooks/webhooks.actions";
import { AttioQuoteMigrationActions } from "../../pages/admin/developer/attio-quote-migration/attio-quote-migration.actions";
import { AttioRecordMigrationActions } from "../../pages/admin/developer/attio-record-migration/attio-record-migration.actions";
import { CurrenciesActions } from "../../pages/admin/super-user/currencies/currencies.actions";
import { InterestRatesActions } from "../../pages/admin/super-user/interest-rates/interest-rates.actions";
import { DataCurrenciesActions } from "../data/currency/currencies/currencies.actions";
import { DataInterestRatesActions } from "../data/interest-rate/interest-rates/interest-rates.actions";
import { DataFinancingCompaniesActions } from "../data/financing-company/financing-companies/financing-companies.actions";
import { CatalogManagementActions } from "../../pages/admin/contract-admin/catalog-management/catalog-management.actions";
import { CompaniesActions } from "../../pages/admin/super-user/companies/companies.actions";
import { FinancingCompaniesActions } from "../../pages/admin/super-user/financing-companies/financing-companies.actions";
import { ReviewQuotesActions } from "../../pages/admin/central-support/review-quotes/review-quotes.actions";
import { CreditCheckActions } from "../data/credit-check/credit-check.actions";
import { DrawingActions } from "../../pages/quote/drawing/drawing.actions";
import { CatalogActions } from "../../pages/catalog/catalog.actions";
import { NotificationActions } from "../notification/notification.actions";

// **************************************************************************
// FEEDBACK EFFECTS
// Handles UI feedback: toast notifications, progress dialogs, success dialogs
// **************************************************************************

@Injectable()
export class FeedbackEffects {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly actions$ = inject(Actions);
    private readonly messageService = inject(MessageService);
    private readonly translate = inject(TranslateService);
    private readonly dialogService = inject(DialogService);

    // =========================================================================
    // DIALOG STATE
    // =========================================================================
    private progressDialogRef: DynamicDialogRef | null = null;
    private progressDialogData$ = new BehaviorSubject<ProgressDialogData | null>(null);

    // =========================================================================
    // TOAST NOTIFICATIONS - Success
    // =========================================================================
    showSuccessToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    // Quote - endast create (updates visas via diskettikon)
                    // duplicateQuoteSuccess moved to progress dialog
                    QuoteCreateActions.createSuccess,
                    // Version - endast create
                    QuoteVersionActions.createNewVersionSuccess,
                    // Items - INTE längre toast, ersatt av operation-indicators
                    // Men batch change product visar toast eftersom det är en större operation
                    ArticlesActions.batchChangeCatalogProductsSuccess,
                    // Customer (visas INTE i quote-detail, behöver toast)
                    CustomerActions.updateCustomerSuccess,
                    CustomerActions.removeCustomerSuccess,
                    CustomerActions.createDeliveryAddressSuccess,
                    // Contact
                    ContactActions.createContactSuccess,
                    ContactActions.updateContactSuccess,
                    ContactActions.removeContactSuccess,
                    ContactActions.addToCustomerSuccess,
                    ContactActions.removeFromCustomerSuccess,
                    // User (visas INTE i quote-detail, behöver toast)
                    UserActions.updatePhoneNumberSuccess,
                    // Admin - Super User
                    CurrenciesActions.updateCurrencySuccess,
                    InterestRatesActions.updateInterestRateSuccess,
                    CatalogManagementActions.uploadCatalogImageSuccess,
                    CompaniesActions.createCompanySuccess,
                    CompaniesActions.updateCompanySuccess,
                    FinancingCompaniesActions.createFinancingCompanySuccess,
                    FinancingCompaniesActions.updateFinancingCompanySuccess,
                    FinancingCompaniesActions.deleteFinancingCompanySuccess,
                    // Vendor (mass update)
                    QuoteVersionActions.updateVendorSuccess,
                    // Notifications (centralized store)
                    NotificationActions.createSuccess,
                    NotificationActions.updateSuccess,
                    NotificationActions.publishSuccess,
                    NotificationActions.unpublishSuccess,
                    NotificationActions.deleteSuccess,
                    // Catalog
                    CatalogActions.duplicateEntitySuccess,
                    CatalogActions.pasteEntitySuccess,
                    // Comments
                    QuoteActions.createCommentSuccess,
                    QuoteActions.deleteCommentSuccess,
                    // Webhooks
                    WebhookActions.createWebhookSuccess,
                    WebhookActions.deleteWebhookSuccess,
                    AttioQuoteMigrationActions.startSuccess,
                    AttioRecordMigrationActions.startSuccess,
                    // Attio sync
                    QuoteActions.syncToAttioSuccess,
                    CustomerActions.syncFromAttioSuccess,
                    ContactActions.syncFromAttioSuccess,
                ),
                tap((action: { toastKey: string }) => {
                    this.messageService.add({
                        severity: "success",
                        summary: this.translate.instant("TOAST.SUCCESS"),
                        detail: this.translate.instant(action.toastKey),
                        life: 3000,
                    });
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // TOAST NOTIFICATIONS - Error
    // =========================================================================
    showErrorToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    // Quote (utan dialog)
                    QuoteActions.initializeQuoteFailure,
                    QuoteActions.updateFailure,
                    QuoteVersionActions.updateVersionFailure,
                    ProductCatalogActions.loadFailure,
                    // Articles
                    ArticlesActions.createArticleFailure,
                    ArticlesActions.batchCreateArticlesFailure,
                    ArticlesActions.batchChangeCatalogProductsFailure,
                    ArticlesActions.updateArticleFieldFailure,
                    ArticlesActions.deleteArticleFailure,
                    ArticlesActions.duplicateArticleFailure,
                    ArticlesActions.duplicateArticleGroupFailure,
                    ArticlesActions.pasteArticleFailure,
                    ArticlesActions.moveArticleFailure,
                    ArticlesActions.refreshArticlesFailure,
                    ArticlesActions.uploadArticleImageFailure,
                    ArticlesActions.importArticlesFailure,
                    // Admin - Developer
                    TransferQuotesActions.transferQuotesFailure,
                    WebhookActions.createWebhookFailure,
                    WebhookActions.deleteWebhookFailure,
                    AttioQuoteMigrationActions.loadPreviewFailure,
                    AttioQuoteMigrationActions.startFailure,
                    AttioQuoteMigrationActions.loadStatusFailure,
                    AttioRecordMigrationActions.loadPreviewFailure,
                    AttioRecordMigrationActions.startFailure,
                    AttioRecordMigrationActions.loadStatusFailure,
                    // Notifications (centralized store)
                    NotificationActions.loadUserNotificationsFailure,
                    NotificationActions.loadReleaseNotesFailure,
                    NotificationActions.loadAdminListFailure,
                    NotificationActions.loadByIdFailure,
                    NotificationActions.createFailure,
                    NotificationActions.updateFailure,
                    NotificationActions.publishFailure,
                    NotificationActions.unpublishFailure,
                    NotificationActions.deleteFailure,
                    // Admin - Super User
                    DataCurrenciesActions.loadFailure,
                    CurrenciesActions.updateCurrencyFailure,
                    DataInterestRatesActions.loadFailure,
                    InterestRatesActions.updateInterestRateFailure,
                    CatalogManagementActions.uploadCatalogImageFailure,
                    CompaniesActions.loadFailure,
                    CompaniesActions.createCompanyFailure,
                    CompaniesActions.updateCompanyFailure,
                    DataFinancingCompaniesActions.loadFailure,
                    FinancingCompaniesActions.createFinancingCompanyFailure,
                    FinancingCompaniesActions.updateFinancingCompanyFailure,
                    FinancingCompaniesActions.deleteFinancingCompanyFailure,
                    // Customer
                    CustomerActions.initializeCustomerFailure,
                    CustomerActions.updateCustomerFailure,
                    CustomerActions.removeCustomerFailure,
                    CustomerActions.searchCustomersFailure,
                    CustomerActions.createCustomerFailure,
                    CustomerActions.createDeliveryAddressFailure,
                    // Contact
                    ContactActions.initializeContactFailure,
                    ContactActions.updateContactFailure,
                    ContactActions.removeContactFailure,
                    ContactActions.createContactFailure,
                    ContactActions.addToCustomerFailure,
                    ContactActions.removeFromCustomerFailure,
                    // User
                    UserActions.updatePhoneNumberFailure,
                    // Central Support
                    ReviewQuotesActions.loadFailure,
                    // Credit check
                    CreditCheckActions.runSubscriptionCheckFailure,
                    // Drawing
                    DrawingActions.loadDrawingFailure,
                    DrawingActions.uploadAttachmentFailure,
                    DrawingActions.deleteAttachmentFailure,
                    DrawingActions.sendForDrawingFailure,
                    DrawingActions.startDrawingFailure,
                    DrawingActions.completeDrawingFailure,
                    DrawingActions.revokeDrawingFailure,
                    // Catalog
                    CatalogActions.createEntityFailure,
                    CatalogActions.updateEntityFieldFailure,
                    CatalogActions.deleteEntityFailure,
                    CatalogActions.uploadEntityImageFailure,
                    CatalogActions.duplicateEntityFailure,
                    CatalogActions.pasteEntityFailure,
                    // Product catalog gating (subscription blocked states)
                    ProductCatalogActions.blocked,
                    // Preview & Quote Options
                    QuoteVersionActions.updateQuoteOptionsFailure,
                    QuoteVersionActions.patchIncludeCustomerPriceDecimalsFailure,
                    QuoteActions.downloadPdfFailure,
                    // Comments
                    QuoteActions.createCommentFailure,
                    QuoteActions.deleteCommentFailure,
                    // Attio sync
                    QuoteActions.syncToAttioFailure,
                    CustomerActions.syncFromAttioFailure,
                    ContactActions.syncFromAttioFailure,
                ),
                tap((action: { toastKey: string; response?: { latestOperation?: { errorCode?: string; errorMessage?: string } }; error?: unknown }) => {
                    const op = action.response?.latestOperation;
                    // Prefer the localized errorCode translation over the backend's English errorMessage
                    // (concatenating both produces a Swedish/English duplicate). If translate returns
                    // the key unchanged, no translation exists — fall back to toastKey + errorMessage.
                    const translatedCode = op?.errorCode ? this.translate.instant(op.errorCode) : null;
                    const hasLocalizedCode = !!translatedCode && translatedCode !== op?.errorCode;

                    let detail: string;
                    if (hasLocalizedCode) {
                        detail = translatedCode!;
                    } else {
                        const fallback = this.translate.instant(action.toastKey);
                        detail = op?.errorMessage ? `${fallback}: ${op.errorMessage}` : fallback;
                    }

                    this.messageService.add({
                        severity: "error",
                        summary: this.translate.instant("TOAST.ERROR"),
                        detail,
                        life: 6000,
                    });
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // TOAST NOTIFICATIONS - Warning (Catalog field warnings)
    // =========================================================================
    showWarningToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CatalogActions.updateEntityFieldSuccess),
                tap((action) => {
                    const warnings = action.response?.latestOperation?.warnings;
                    if (warnings?.length) {
                        for (const warning of warnings) {
                            this.messageService.add({
                                severity: "warn",
                                summary: this.translate.instant("TOAST.WARNING"),
                                detail: warning,
                                life: 10000,
                            });
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // TOAST NOTIFICATIONS - Info
    // =========================================================================
    showInfoToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    // Admin - Developer
                    TransferQuotesActions.transferQuotesSuccess,
                    // Vendor (mass update) - info when starting
                    QuoteVersionActions.updateVendor,
                ),
                filter((action: { toastKey?: string }) => !!action.toastKey),
                tap((action: { toastKey?: string }) => {
                    this.messageService.add({
                        severity: "info",
                        summary: this.translate.instant("TOAST.INFO"),
                        detail: this.translate.instant(action.toastKey!),
                        life: 3000,
                    });
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // TOAST NOTIFICATIONS - Info (Center)
    // =========================================================================
    showCenterInfoToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    QuoteVersionActions.updateManagementAutoCalculation,
                ),
                filter((action: { toastKey?: string }) => !!action.toastKey),
                tap((action: { toastKey?: string }) => {
                    this.messageService.add({
                        key: "center",
                        severity: "info",
                        summary: this.translate.instant("TOAST.INFO"),
                        detail: this.translate.instant(action.toastKey!),
                        life: 3000,
                    });
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // PROGRESS DIALOGS - Pending
    // =========================================================================
    showPendingDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    QuoteActions.send,
                    QuoteActions.createOrder,
                    QuoteActions.updateToUnderReview,
                    QuoteActions.updateToReadyForReview,
                    QuoteActions.updateBackToSent,
                    QuoteActions.updateMarkAsLost,
                    QuoteActions.extendValidity,
                    QuoteActions.downloadPdf,
                    QuoteActions.duplicateQuote,
                ),
                tap((action) => {
                    this.openProgressDialog(action.dialogKey, 'PENDING');
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // PROGRESS DIALOGS - Success
    // =========================================================================
    showSuccessDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    QuoteActions.sendSuccess,
                    QuoteActions.updateToUnderReviewSuccess,
                    QuoteActions.updateToReadyForReviewSuccess,
                    QuoteActions.updateBackToSentSuccess,
                    QuoteActions.updateMarkAsLostSuccess,
                    QuoteActions.extendValiditySuccess,
                    QuoteActions.downloadPdfSuccess,
                ),
                tap((action) => {
                    this.openProgressDialog(action.dialogKey, 'SUCCESS');
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // SEND QUOTE SUCCESS DIALOG
    // =========================================================================
    showSendQuoteSuccessDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuoteActions.sendSuccess),
                tap(action => {
                    const messageKey = action.sendToSelf ? 'MESSAGE' : 'MESSAGE_CUSTOMER';
                    this.openProgressDialog(action.dialogKey, 'SUCCESS', undefined, undefined, messageKey);
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // ORDER SUCCESS DIALOG
    // =========================================================================
    showOrderSuccessDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuoteActions.createOrderSuccess),
                tap((action) => {
                    if (this.progressDialogRef) {
                        this.progressDialogRef.close();
                        this.progressDialogRef = null;
                    }

                    this.dialogService.open(OrderSuccessDialogComponent, {
                        data: { quote: action.quote, dialogKey: action.dialogKey },
                        closable: true,
                        modal: true,
                        showHeader: false,
                        styleClass: 'order-success-dialog',
                    });
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // DUPLICATE QUOTE SUCCESS - Close dialog and let navigation effect handle redirect
    // =========================================================================
    closeDuplicateQuoteDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuoteActions.duplicateQuoteSuccess),
                tap(() => {
                    if (this.progressDialogRef) {
                        this.progressDialogRef.close();
                        this.progressDialogRef = null;
                    }
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // PROGRESS DIALOGS - Failure (Send Quote with specific error handling)
    // =========================================================================
    showSendFailureDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuoteActions.sendFailure),
                tap((action) => {
                    // normalizeError in the send effect lifts the API response body
                    // (BadRequest(ex.Message)) into message, so the codes are matchable here.
                    const errorMessage = action.error?.message ?? '';
                    if (errorMessage.includes('TERMS_NOT_FOUND')) {
                        this.openProgressDialog(action.dialogKey, 'FAILURE', undefined, 'TERMS_NOT_FOUND');
                    } else if (errorMessage.includes('CREDIT_CHECK_DENIED')) {
                        // Defence in depth: the step dialog normally stops this earlier, so reaching
                        // here means stale client state or a direct API call.
                        this.openProgressDialog(action.dialogKey, 'FAILURE', undefined, 'CREDIT_CHECK_DENIED');
                    } else if (errorMessage.includes('TG_BELOW_MIN_THRESHOLD')) {
                        this.openProgressDialog(action.dialogKey, 'FAILURE', undefined, 'TG_BELOW_MIN_THRESHOLD');
                    } else {
                        this.openProgressDialog(action.dialogKey, 'FAILURE');
                    }
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // PROGRESS DIALOGS - Failure (Generic)
    // =========================================================================
    showFailureDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    QuoteActions.createOrderFailure,
                    QuoteActions.updateToUnderReviewFailure,
                    QuoteActions.updateToReadyForReviewFailure,
                    QuoteActions.updateBackToSentFailure,
                    QuoteActions.updateMarkAsLostFailure,
                    QuoteActions.extendValidityFailure,
                    QuoteActions.downloadPdfFailure,
                    QuoteActions.duplicateQuoteFailure,
                ),
                tap((action) => {
                    this.openProgressDialog(action.dialogKey, 'FAILURE');
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // PRIVATE METHODS - Progress Dialog
    // =========================================================================
    private openProgressDialog(
        dialogKey: string,
        status: 'PENDING' | 'SUCCESS' | 'FAILURE',
        response?: string,
        errorCode?: string,
        messageKey?: string,
    ): void {
        let title: string;
        let message: string;

        if (errorCode) {
            title = this.translate.instant(`EFFECTS_DIALOGS.${dialogKey}.${status}.${errorCode}.TITLE`);
            message = this.translate.instant(`EFFECTS_DIALOGS.${dialogKey}.${status}.${errorCode}.MESSAGE`);
        } else {
            title = this.translate.instant(`EFFECTS_DIALOGS.${dialogKey}.${status}.TITLE`);
            message = this.translate.instant(`EFFECTS_DIALOGS.${dialogKey}.${status}.${messageKey ?? 'MESSAGE'}`);
        }

        const data: ProgressDialogData = {
            title,
            message,
            status,
            response,
        };

        this.progressDialogData$.next(data);

        if (!this.progressDialogRef) {
            this.progressDialogRef = this.dialogService.open(ProgressDialogComponent, {
                data: { data$: this.progressDialogData$.asObservable() },
                closable: false,
                modal: true,
                showHeader: false,
                styleClass: 'progress-dialog',
            });

            this.progressDialogRef?.onClose.subscribe(() => {
                this.progressDialogRef = null;
            });
        }
    }
}
